import { inject, Injectable } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

import {
  TrialInsightsInvestigatorCostResponseType,
  TrialInsightsResponseGeneric,
  TrialInsightsState,
} from '@models/trial-insights/trial-insights-store.model';
import { TrialInsightsStore } from '@models/trial-insights/trial-insights.store';

import { GqlService } from '@shared/services/gql.service';
import { GenericOperator } from '@shared/services/operator.service';
import { MainQuery } from '@shared/store/main/main.query';

type LoadingState = 'completed' | 'initial';
type Response = TrialInsightsResponseGeneric<ResponseType[]>;
type ResponseType = TrialInsightsInvestigatorCostResponseType;
type State = Partial<TrialInsightsState['investigatorCost']>;

@Injectable({
  providedIn: 'root',
})
export class TrialInsightsClinicalInvestigatorCostStoreService {
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private store = inject(TrialInsightsStore);

  getTotalInvestigatorCosts$(): Observable<unknown> {
    return this.mainQuery.select('trialKey').pipe(
      this.updateLoadingState('initial'),
      switchMap(() => this.gqlService.getTotalInvestigatorCosts$().pipe(take(1))),
      this.processResponse(),
      this.updateLoadingState('completed')
    );
  }

  loadingState(type: LoadingState): State {
    const loadingState: State = {};

    if (type === 'initial') {
      loadingState.isLoading = true;
      loadingState.data = null;
    } else {
      loadingState.isLoading = false;
    }

    return loadingState;
  }

  processResponse() {
    const processFn = (response: Response, subscriber: Subscriber<unknown>) => {
      const { data } = response;

      this.store.update((state) => {
        return {
          ...state,
          investigatorCost: {
            ...state.investigatorCost,
            data,
          },
        };
      });

      subscriber.next(data);
    };

    const operatorConfig = new GenericOperator(processFn);

    return operatorConfig.operator();
  }

  updateLoadingState(type: LoadingState) {
    const updates = this.loadingState(type);

    const processFn = (_: unknown, subscriber: Subscriber<unknown>) => {
      this.store.update((state) => {
        return {
          ...state,
          investigatorCost: {
            ...state.investigatorCost,
            ...updates,
          },
        };
      });

      subscriber.next(updates);
    };

    const operatorConfig = new GenericOperator(processFn);

    return operatorConfig.operator();
  }
}
